import { AiCrawlerLineup, AiCrawlerProvider, mapNormalizedToProvider, MissingCrawlerDomain } from '../shared';
import type {
  DiscoverabilityStatusDomain,
  DiscoverabilityStatusResponse,
  EvaluableDiscoverabilityStatusDomain,
} from '../discoverability/api/discoverabilityStatusApi';

export type MissingCrawlersDerivation = {
  integratedDomains: string[];
  totalAffectedDomains: number;
  totalUndetectedCrawlers: number;
  domains: MissingCrawlerDomain[];
};

function isEvaluable(
  domain: DiscoverabilityStatusDomain,
): domain is EvaluableDiscoverabilityStatusDomain {
  return domain.warmingUp === false;
}

function missingProvidersFor(
  lineup: AiCrawlerLineup,
  domain: EvaluableDiscoverabilityStatusDomain,
): AiCrawlerProvider[] {
  const providers = new Set<AiCrawlerProvider>();
  domain.missingCrawlers.forEach((label) => {
    const provider = mapNormalizedToProvider(lineup, label);
    if (provider) {
      providers.add(provider);
    }
  });

  return lineup.providers.filter((provider) => providers.has(provider));
}

export function deriveMissingCrawlers(
  lineup: AiCrawlerLineup,
  status: DiscoverabilityStatusResponse,
): MissingCrawlersDerivation {
  const evaluableDomains = status.domains.filter(isEvaluable);

  const affectedDomains: MissingCrawlerDomain[] = [];
  const distinctProviders = new Set<AiCrawlerProvider>();

  evaluableDomains.forEach((domain) => {
    const missingCrawlers = missingProvidersFor(lineup, domain);
    if (missingCrawlers.length === 0) {
      return;
    }

    affectedDomains.push({ domain: domain.domain, missingCrawlers });
    missingCrawlers.forEach((provider) => distinctProviders.add(provider));
  });

  return {
    integratedDomains: evaluableDomains.map((domain) => domain.domain),
    totalAffectedDomains: affectedDomains.length,
    totalUndetectedCrawlers: distinctProviders.size,
    domains: affectedDomains,
  };
}
